import { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";

import { security, type SecurityEvent } from "@/api/client";
import { Chip, type ChipTone } from "@/components/Chip";
import { DataTable, type DataCol } from "@/components/DataTable";
import { ErrorNote } from "@/components/ErrorNote";
import { Field } from "@/components/Field";
import { PageHead } from "@/components/PageHead";
import { SearchInput, Select } from "@/components/inputs";
import { ago } from "@/lib/format";
// The catalogue owns kind labels (and since 0064 they come from the DATABASE),
// so a product added in Products reads correctly here for free.
import { kindLabel, useTemplates } from "@/modules/asset_catalog/common";

/** The security audit log: what was minted, for what, by whom, when.
 *
 *  Dattatray: *"Secutiy audit log : What wasminted for which asset/Component
 *  /part / I should also be able to add filters and see what exactly happned
 *  for a specifc device and when!"* and *"Dont show the secret that was minted
 *  on UI / Just show log on what was minted for by who and when and for what
 *  purpose etc..!"*
 *
 *  NO SECRET REACHES THIS FILE, and not because the page declines to render
 *  one. The server's audit view names its columns and the key material is not
 *  among them, so there is no field here to leak — `tests/security` compiles
 *  the actual SQL and asserts the projection. A page that received the key and
 *  chose not to show it would be one `console.log` from publishing it.
 *
 *  WHAT A SERIAL IS. The certificate serial IS shown, and it is not a secret:
 *  it is printed in the certificate, which is the public half. It is also the
 *  only handle an operator has for "revoke this exact certificate".
 *
 *  ONE TIMELINE, NOT TWO TABLES. A board's certificate and its BLE key are two
 *  answers to "what happened to this device", and the cases that matter are
 *  the ones where their ORDER matters.
 */

const EVENT_TONE: Record<string, ChipTone> = {
  minted: "ok", revoked: "bad", superseded: "warn",
};

/** What each event means, in the console's own words. Hover text, because the
 *  difference between "revoked" and "superseded" is exactly the thing an
 *  operator reading an incident needs and would otherwise guess at. */
const EVENT_MEANS: Record<string, string> = {
  minted: "Orbit issued this secret and handed it to the EOL tool once",
  revoked: "deliberately killed. The device stops being accepted on its next connect",
  superseded: "replaced by a newer key. Rotation closes the old row rather than overwriting it",
};

export function AuditLogPage() {
  const [q, setQ] = useState("");
  const [kind, setKind] = useState("");
  const [event, setEvent] = useState("");
  // ── WHAT KIND OF DEVICE, AND WHO ──────────────────────────────────────
  // Dattatray: *"we should have filters for product, Model, Type of mint
  // (mTLS /BLE rootkey, Whom) so that it is easier to put the filters and
  // underatdn what is happening."*
  //
  // `what` IS the type of mint — the server already labels every event "mTLS
  // certificate" or "BLE root key" — so this filters that field rather than
  // inventing a parallel vocabulary for the same distinction. It is NOT the
  // same question as the Device/Part filter beside it, which is what the
  // subject is; a board can hold both kinds of credential.
  const [product, setProduct] = useState("");
  const [model, setModel] = useState("");
  const [what, setWhat] = useState("");
  const [actor, setActor] = useState("");

  // NO POLL. An audit log of EOL mints changes when somebody runs a factory
  // tool, not continuously, and the UI performance laws budget poll cadence
  // for live views. Refetching this every few seconds would spend the budget
  // on a page that is almost always identical to its last render.
  const log = useQuery({
    queryKey: ["security", "audit", q],
    // AUDIT_LIMIT PASSED EXPLICITLY, not left to the client's own default.
    // The banner below claims a specific number out loud; if the request used
    // one constant and the sentence another, the page could truthfully say
    // "newest 200" while holding 500 — a wrong statement about completeness in
    // the one view whose job is to be complete.
    queryFn: () => security.audit(q || undefined, AUDIT_LIMIT),
  });
  const status = useQuery({
    queryKey: ["security", "status"],
    queryFn: security.status,
  });
  const tpl = useTemplates().data;
  const cols = useAuditCols(tpl);

  // CLIENT-SIDE, deliberately. The server already filters by identifier (the
  // expensive, index-backed one); the rest are a handful of values over an
  // already-fetched page, so a round trip per dropdown change would be
  // latency for nothing.
  //
  // THE HONEST LIMIT OF THAT: the server returns the newest `LIMIT` events, so
  // these filters narrow a WINDOW, not the whole history. With a few hundred
  // mints that is the whole history; once it is not, the page says so (the
  // banner below fires when the response came back full) rather than quietly
  // showing a subset of a subset.
  const rows = useMemo(() => (log.data ?? []).filter(
    (e) => (!kind || e.subject_kind === kind)
      && (!event || e.event === event)
      && (!product || e.kind === product)
      // By model_id, not by the displayed name: one model can have several
      // catalog versions under the same name, and a name filter would group
      // them by coincidence rather than because they are the same model.
      && (!model || e.model_id === model)
      && (!what || e.what === what)
      && (!actor || (e.actor ?? "") === actor)),
    [log.data, kind, event, product, model, what, actor]);

  // Options come from the ROWS, not from a catalog listing: a product with no
  // mints in this window is not a filter, it is a dead end. Same rule the FOTA
  // pickers follow. Models are narrowed by the chosen product so the two
  // dropdowns cannot be set to a contradiction.
  const opts = useMemo(() => {
    const es = log.data ?? [];
    const uniq = (xs: (string | null)[]) =>
      [...new Set(xs.filter((x): x is string => !!x))].sort();
    return {
      products: uniq(es.map((e) => e.kind)),
      // (id, name) pairs: the option's VALUE is the model id so the filter is
      // exact, its LABEL is the name so it is readable.
      models: [...new Map(es
        .filter((e) => (!product || e.kind === product) && e.model_id)
        .map((e) => [e.model_id as string, e.model ?? (e.model_id as string)]))]
        .sort((a, b) => a[1].localeCompare(b[1])),
      whats: uniq(es.map((e) => e.what)),
      actors: uniq(es.map((e) => e.actor)),
    };
  }, [log.data, product]);

  return (
    <div className="mpage on">
      <PageHead
        eyebrow="Security"
        title="Audit log"
        desc="Every secret Orbit has minted or revoked, and who asked for it.
              The secrets themselves are never shown here, and the device
              private keys are never stored at all."
      />

      {/* THE HONEST STATE OF THIS BOX. Without it an empty table reads as
          "nothing issued" when it may mean "this box cannot issue". */}
      {status.data && !status.data.provisioning_enabled && (
        <div className="panel panel-pad" style={{ marginBottom: 12,
              borderLeft: "3px solid var(--warn)" }}>
          <b style={{ color: "var(--warn)" }}>Provisioning is off on this box</b>
          <div style={{ fontSize: 12.5, marginTop: 4, color: "var(--t-mute)" }}>
            Nothing can be minted here. The log below is the real history, and
            it stays empty until the EOL provisioning flow is switched on after
            firmware integration testing.
          </div>
        </div>
      )}

      {/* `filterrow` + `Field`, not a hand-rolled flex row with six bare
          Selects. Six controls is exactly where a hand-rolled row goes ragged
          -- the fields share the slack and rebalance instead of one orphaning
          onto a line of its own at 150% scaling -- and the labels are what
          make six dropdowns readable at all: an unlabelled <select> reading
          "All products" is a guess about what it filters. */}
      <div className="panel panel-pad filterrow" style={{ marginBottom: 12 }}>
        {/* ONE SEARCH BOX FOR EVERY IDENTIFIER. Somebody holding a board knows
            its serial, somebody on a ticket knows the asset, somebody in a
            broker log knows the IMEI. Making them choose the field first is
            making them translate. */}
        <Field label="Find">
          <SearchInput mono className="fill" value={q}
            placeholder="IMEI, board serial, BLE MAC, asset or cert serial"
            onChange={(e) => setQ(e.target.value)} />
        </Field>
        <Field label="Product">
          <Select className="fill" value={product}
            onChange={(e) => { setProduct(e.target.value); setModel(""); }}>
            <option value="">All products</option>
            {opts.products.map((k) => (
              <option key={k} value={k}>{kindLabel(tpl, k)}</option>
            ))}
          </Select>
        </Field>
        <Field label="Model">
          <Select className="fill" value={model}
            onChange={(e) => setModel(e.target.value)}>
            <option value="">All models</option>
            {opts.models.map(([id, name]) => (
              <option key={id} value={id}>{name}</option>
            ))}
          </Select>
        </Field>
        <Field label="Credential">
          <Select className="fill" value={what} onChange={(e) => setWhat(e.target.value)}>
            <option value="">Every credential</option>
            {opts.whats.map((w) => <option key={w} value={w}>{w}</option>)}
          </Select>
        </Field>
        <Field label="Minted by">
          <Select className="fill" value={actor} onChange={(e) => setActor(e.target.value)}>
            <option value="">Anyone</option>
            {opts.actors.map((a) => <option key={a} value={a}>{a}</option>)}
          </Select>
        </Field>
        <Field label="Subject">
          <Select className="fill" value={kind} onChange={(e) => setKind(e.target.value)}>
            <option value="">Device and part</option>
            <option value="device">Device (modem)</option>
            <option value="part">Part (board)</option>
          </Select>
        </Field>
        <Field label="Event">
          <Select className="fill" value={event} onChange={(e) => setEvent(e.target.value)}>
            <option value="">Every event</option>
            <option value="minted">Minted</option>
            <option value="revoked">Revoked</option>
            <option value="superseded">Superseded</option>
          </Select>
        </Field>
        <span className="scope-act cap">
          {rows.length} of {(log.data ?? []).length}
        </span>
      </div>

      {log.isError && <ErrorNote error={log.error} />}

      {/* THE WINDOW, SAID OUT LOUD. The response came back exactly full, so
          there are almost certainly older events this page is not holding --
          and the dropdowns above filter what it holds. Without this line a
          filtered view of the newest 200 is indistinguishable from a filtered
          view of everything, which in an AUDIT log is the one ambiguity that
          must not exist. */}
      {(log.data?.length ?? 0) >= AUDIT_LIMIT && (
        <div className="panel panel-pad cap" style={{ marginBottom: 10,
              borderLeft: "3px solid var(--warn)" }}>
          Showing the newest {AUDIT_LIMIT} events. The filters above narrow
          these, not the whole history. Use the search box to reach older
          events for a specific device or serial.
        </div>
      )}

      <DataTable rows={rows} cols={cols}
        rowKey={(e) => `${e.ts}-${e.subject}-${e.event}`}
        maxHeight={620} pageSize={100}
        empty={<div className="panel panel-pad cap">
          Nothing has been minted yet.
        </div>} />
    </div>
  );
}

/** How many events the server hands back. Named here because the PAGE has to
 *  say so when the window is full — see the banner. */
const AUDIT_LIMIT = 200;

/** The columns, built with `useMemo` rather than as a module const.
 *
 *  The const was deliberate and its reason still holds — rebuilding this array
 *  on every render remounts every row on every keystroke in the search box
 *  (UI law: stable keys + memoized rows). A `useMemo` keyed on the kind
 *  templates keeps that property: `tpl` changes once, when the catalog
 *  response lands, so the array is identity-stable for the life of the page
 *  and typing does not touch it.
 *
 *  It had to move because the Product column labels a kind through
 *  `kindLabel(tpl, …)`, and `tpl` comes from a hook. The alternative was
 *  printing the raw `cluster_c` in an operator-facing column. */
function useAuditCols(tpl: ReturnType<typeof useTemplates>["data"]) {
  return useMemo<DataCol<SecurityEvent>[]>(() => [
  { key: "when", label: "When", width: 130, priority: 1,
    title: (e) => e.ts ?? "",
    render: (e) => (e.ts ? `${ago(e.ts)} ago` : "—") },
  { key: "event", label: "Event", width: 120, priority: 1,
    title: (e) => EVENT_MEANS[e.event] ?? e.event,
    render: (e) => <Chip tone={EVENT_TONE[e.event] ?? "mute"}>
      {e.event.toUpperCase()}
    </Chip> },
  { key: "what", label: "What", width: 150, priority: 1, render: (e) => e.what },
  // TWO COLUMNS, NOT ONE. Dattatray asked for "type of device (product and
  // model)"; merged into a single cell that reads
  // "Cluster · AE-CLUC-SEG-LCD-B-E1" it ellipsises to nothing at any width a
  // ten-column table can spare, and the two have different jobs — Product is
  // the 1-of-a-handful value you SCAN a log by, Model is the specific thing
  // you confirm once you have found the row. So Product holds its space and
  // Model is the first of the pair to be dropped on a narrow panel.
  { key: "product", label: "Product", width: 130, priority: 2,
    render: (e) => (e.kind ? kindLabel(tpl, e.kind) : "—"),
    title: (e) => e.kind ?? "no product recorded on this mint" },
  { key: "model", label: "Model", width: 170, mono: true, priority: 3,
    render: (e) => e.model ?? "—",
    title: (e) => e.model ?? "no model version recorded on this mint" },
  { key: "subject", priority: 1, label: "For", width: 170, mono: true,
    title: (e) => `${e.subject_kind}: ${e.subject}`,
    render: (e) => e.subject },
  { key: "asset", priority: 2, label: "Asset", width: 140, mono: true,
    // NOT "unknown": a mint can legitimately predate the part being fitted to
    // anything, which is the normal case at end of line.
    render: (e) => e.asset_id ?? "—" },
  { key: "actor", priority: 2, label: "By", width: 150,
    render: (e) => e.actor ?? "—" },
  { key: "detail", priority: 3, label: "Detail", width: 230, mono: true,
    title: (e) => e.detail,
    render: (e) => e.detail || "—" },
  { key: "note", priority: 3, label: "Purpose", width: 220,
    title: (e) => e.note ?? "",
    render: (e) => e.note ?? "—" },
  ], [tpl]);
}
